/**
 * IMPORTS
 */
import {MessageTypes} from 'src/aggregates/conversations/message/messages.d';
import {fetchEmail} from 'src/aggregates/conversations/utils';
import {logger} from 'src/logger';
import {getDocumentName} from 'src/utils/downloadFile';
import vCard from 'vcard-parser';


/**
 * TYPES
 */
import {IAttachment} from 'src/aggregates/conversations/state.d';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';
import {IMessage as ITimelineMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I reduce a payload.
 *
 * :param data: message data
 *
 * :returns: promise with reduced conversation or null
 */
const reducePayload = async (
    data: any,
): Promise<ITimelineMessage | null> =>
{
    // payload types with JSON format body data
    const JSON_PAYLOADS = [
        MessageTypes.CALL,
        MessageTypes.CONTACT,
        MessageTypes.INTERACTIVE_LIST,
        MessageTypes.INTERACTIVE_OPTION,
        MessageTypes.LOCATION,
    ];

    // set parsed JSON value
    let parsed;

    // payload body is in JSON format: parse it
    if (JSON_PAYLOADS.includes(data.type))
    {
        // try to reduce message body
        try
        {
            parsed = JSON.parse(data.body);
        }

        // error parsing: log error
        catch (error)
        {
            logger.error('CVJPZ0041E', {
                id: data.id,
                reason: error,
                user: fetchUser().id,
            });
        }

        // message is a contact: set reduced body as parsed vcard
        if (data.type === MessageTypes.CONTACT)
        {
            // reduce vcard from message body, or empty array if undefined
            parsed = parsed ? parsed.map((vcard: string) => vCard.parse(vcard))
                            : [];

        }
    }

    // message deleted information not provided: set it to null
    if (data.deleted === undefined)
    {
        data.deleted = null;
    }

    // message deleted: convert to milliseconds
    if (data.deleted !== null)
    {
        data.deleted = data.deleted * 1000;
    }

    // has referral field: parse it
    if (data.referral !== null && data.referral !== undefined)
    {
        // try to parse referral
        try
        {
            data.referral = JSON.parse(data.referral);
        }

        // error parsing: log error
        catch (error)
        {
            logger.error('CVJPZ0041E', {
                id: data.id,
                reason: error,
                user: fetchUser().id,
            });
        }
    }

    // create attachments fields for message, default it to empty array
    const messageAttachments: IAttachment[] = [];

    // document payload: use attachment to set file name
    if (data.type === MessageTypes.FILE)
    {
        // set file attachment
        const fileAttachment: IAttachment =
        {
            id: data.body,
            mime: '',
            name: await getDocumentName(data.body),
            thumbnail: null,
        };

        // add created attachment
        messageAttachments.push(fileAttachment);
    }

    // reduce message
    let message: ITimelineMessage = {
        ack: data.ack,
        attachments: messageAttachments,
        body: data.body,
        caption: data.caption,
        deleted: data.deleted,
        id: data.id,
        mode: data.mode ?? null,
        name: null,
        originalId: data.originalId,
        outgoing: data.outgoing,
        reducedBody: parsed,
        referral: data.referral,
        reply: data.reply,
        timestamp: data.timestamp,
        type: data.type,
    };

    // fetch email message if necessary
    if (message.type === MessageTypes.EMAIL)
    {
        message = await fetchEmail(message);
    }

    // return reduced message
    return message;
};


/**
 * EXPORTS
 */
export
{
    reducePayload,
};
